'use client'

import Link from 'next/link'
import { useCallback, useEffect, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'
import { useLanguage } from '@/context/LanguageContext'
import { useCountries } from '@/context/CountriesContext'
import { t } from '@/i18n/translations'
import Logo from './Logo'

export default function Header() {
    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const countries = useCountries()
    const mobileMenuRef = useRef(null)
    const menuToggleRef = useRef(null)
    const { language, toggleLanguage, isChangingLanguage } = useLanguage()
    const headerRef = useRef(null)
    const initialOffsetRef = useRef(null)
    const pathname = usePathname()

    // Текущая страна из пути: /:lang/:country/...
    const pathParts = (pathname || '').split('/').filter(Boolean)
    const currentLang = pathParts[0] === 'en' || pathParts[0] === 'ru' ? pathParts[0] : language
    const secondSegment = pathParts.length >= 2 ? pathParts[1] : ''
    const topLevelSections = new Set(['esim', 'vpn', 'tours', 'transport', 'stay', 'insurance', 'blog', 'privacy', 'disclaimer', 'cities', 'contact', 'tables'])
    const currentCountry = secondSegment && !topLevelSections.has(secondSegment) ? secondSegment : ''
    const isHomePage = pathname === `/${currentLang}`

    // Рендер завязан на язык из URL (currentLang), а не на клиентский контекст —
    // иначе при прямой загрузке RU-страницы шапка мигает EN→RU после гидрации.
    // toggleLanguage/isChangingLanguage остаются из контекста (это действие, не рендер).
    const ui = t(currentLang, 'miscUi')

    const getCountryName = (country) => {
        if (currentLang === 'ru' && country.nameRu) return country.nameRu
        if (currentLang === 'en' && country.nameEn) return country.nameEn
        return country.name || country.slug
    }

    // Фактическая высота шапки → CSS-переменная --header-offset (используется в других местах)
    useEffect(() => {
        let rafId = null
        const updateHeaderOffsetNow = () => {
            if (!headerRef.current) return
            const height = Math.ceil(headerRef.current.getBoundingClientRect().height)
            if (initialOffsetRef.current == null) initialOffsetRef.current = height
            const next = Math.min(initialOffsetRef.current, height)
            document.documentElement.style.setProperty('--header-offset', `${next}px`)
        }
        const schedule = () => {
            if (rafId) return
            rafId = window.requestAnimationFrame(() => { rafId = null; updateHeaderOffsetNow() })
        }
        updateHeaderOffsetNow()
        window.addEventListener('resize', schedule)
        window.addEventListener('orientationchange', schedule)
        if (window.visualViewport) window.visualViewport.addEventListener('resize', schedule)
        return () => {
            if (rafId) cancelAnimationFrame(rafId)
            window.removeEventListener('resize', schedule)
            window.removeEventListener('orientationchange', schedule)
            if (window.visualViewport) window.visualViewport.removeEventListener('resize', schedule)
        }
    }, [])

    const closeMobileMenu = useCallback(() => {
        setIsMenuOpen(false)
        menuToggleRef.current?.focus()
    }, [])

    // Escape + focus-trap внутри мобильного меню
    useEffect(() => {
        if (!isMenuOpen) return
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') { closeMobileMenu(); return }
            if (e.key === 'Tab' && mobileMenuRef.current) {
                const focusable = mobileMenuRef.current.querySelectorAll('a[href], button:not([disabled])')
                if (focusable.length === 0) return
                const first = focusable[0]
                const last = focusable[focusable.length - 1]
                if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus() }
                else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus() }
            }
        }
        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [isMenuOpen, closeMobileMenu])

    const currentCountryData = currentCountry
        ? countries.find(c => c.slug?.toLowerCase() === currentCountry.toLowerCase())
        : null

    // Ссылки навигации: на странице страны — её сервисы; иначе — Страны / Блог / Контакты.
    const countriesHref = isHomePage ? '#countries' : `/${currentLang}`
    const navLinks = currentCountry
        ? [
            { href: `/${currentLang}/${currentCountry}/cities`, label: ui.nav.cities },
            { href: `/${currentLang}/${currentCountry}/esim`, label: ui.nav.esim },
            { href: `/${currentLang}/${currentCountry}/vpn`, label: ui.nav.vpn },
            { href: `/${currentLang}/${currentCountry}/insurance`, label: ui.nav.insurance },
            { href: `/${currentLang}/${currentCountry}/tours`, label: ui.nav.tours },
            { href: `/${currentLang}/${currentCountry}/transport`, label: ui.nav.transport },
            { href: `/${currentLang}/${currentCountry}/stay`, label: ui.nav.stay },
            { href: `/${currentLang}/blog`, label: ui.nav.blog },
        ]
        : [
            { href: countriesHref, label: ui.nav.countries },
            { href: `/${currentLang}/blog`, label: ui.nav.blog },
            { href: `/${currentLang}/contact`, label: ui.nav.contact },
        ]

    return (
        <header ref={headerRef} className="gf-hdr safe-area-padding-x">
            <div className="gf-hdr-in">
                <Link href={`/${currentLang}`} aria-label="TripLinkHub" onClick={() => setIsMenuOpen(false)}>
                    <Logo />
                </Link>

                <nav className="gf-nav" aria-label={ui.aria.primaryNav}>
                    {navLinks.map((link) => (
                        <Link key={link.href + link.label} href={link.href}>{link.label}</Link>
                    ))}
                </nav>

                <div className="gf-hdr-r">
                    {currentCountryData?.iso2 && currentCountryData.iso2.trim() !== '' && (
                        <Link href={`/${currentLang}/${currentCountry}`} title={getCountryName(currentCountryData)} className="hidden sm:inline-flex">
                            <img
                                src={`https://flagcdn.com/w80/${currentCountryData.iso2.toLowerCase()}.png`}
                                alt={getCountryName(currentCountryData)}
                                className="gf-flag"
                                loading="eager"
                            />
                        </Link>
                    )}

                    <button
                        type="button"
                        onClick={toggleLanguage}
                        disabled={isChangingLanguage}
                        className="gf-lang"
                        aria-label={`${ui.aria.switchLang} (${currentLang.toUpperCase()})`}
                    >
                        {currentLang.toUpperCase()}
                    </button>

                    <button
                        ref={menuToggleRef}
                        type="button"
                        onClick={() => setIsMenuOpen(v => !v)}
                        className={`gf-burger ${isMenuOpen ? 'open' : ''}`}
                        aria-label={isMenuOpen ? ui.aria.closeMenu : ui.aria.openMenu}
                        aria-expanded={isMenuOpen}
                        aria-controls="gf-mobile-menu"
                    >
                        <i /><i /><i />
                    </button>
                </div>
            </div>

            <div id="gf-mobile-menu" ref={mobileMenuRef} className={`gf-mobile ${isMenuOpen ? 'is-open' : ''}`} role="navigation" aria-label={ui.aria.mobileMenu}>
                <div className="gf-mobile-in">
                    {navLinks.map((link) => (
                        <Link key={link.href + link.label} href={link.href} onClick={() => setIsMenuOpen(false)}>{link.label}</Link>
                    ))}
                </div>
            </div>
        </header>
    )
}
